import React, { useEffect, useState, useCallback } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

// Actions
import { SG_GET_CULTURE_AUDIT_REPORTS, SG_FETCH_TASK_SCHEDULE } from "constants/actions";

// Components
import DebriefSelector from "./DebriefSelector";

// Utils copied from PulseSurveyStep to build pulse data
const getRecentResponses = (data, employees, filters, get_employee_categories) => {
  const responses = {};
  const employeeList = employees?.employees;
  if (!data?.task_responses || !Array.isArray(data.task_responses)) {
    return [];
  }
  const filteredEmployeeCategories = get_employee_categories?.filter((employeeCategory) => {
    if (!filters || filters.length === 0) {
      return true;
    }
    return filters.every(filter => {
      const matchingCategory = employeeCategory.category?.find(cat =>
        cat.value_id === filter.id
      );
      return !!matchingCategory;
    });
  })?.map((employeeCategory) => employeeCategory.employee_id) || [];
  data.task_responses.forEach((response) => {
    const employeeId = Number(response.employee);
    const employeeObj = employeeList?.find((emp) => emp.id === employeeId);
    response.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
    if (!responses[employeeId] || response.created_at > responses[employeeId].created_at) {
      responses[employeeId] = response;
    }
  });
  const filteredResponses = Object.values(responses).filter((response) => {
    if (!filters || filters.length === 0) {
      return true;
    }
    return filteredEmployeeCategories.includes(response.employee);
  });
  return filteredResponses;
};

const calculateCompliance = (participants, responses) => {
  if (!participants || participants.length === 0) {
    return 0;
  }
  const participantIds = participants.map(p => p.id);
  const responseEmployeeIds = responses.map(r => Number(r.employee));
  const respondedCount = participantIds.filter(id => responseEmployeeIds.includes(id)).length;
  const compliancePercentage = Math.round((respondedCount / participantIds.length) * 100);
  return {
    percentage: compliancePercentage,
    respondedCount,
    totalParticipants: participantIds.length
  };
};

const buildSurveyStructureData = (core_data) => {
  if (!core_data) {
    return {
      questionStructure: null,
      surveyStructure: null,
      standards: null,
      extractedQuestions: [],
      surveyQues: null
    };
  }
  const questionStructure = core_data?.questions || null;
  const surveyStructure = core_data?.categories?.[0] || null;
  const standards = core_data?.standards || null;
  let extractedQuestions = [];
  let surveyQues = null;
  if (questionStructure?.dimensions) {
    questionStructure.dimensions.forEach((dimension, dimIndex) => {
      if (dimension.subfactors) {
        dimension.subfactors.forEach((subfactor, subfactorIndex) => {
          if (subfactor.questions) {
            subfactor.questions.forEach((question, questionIndex) => {
              extractedQuestions.push({
                id: question.id || `${dimIndex}_${subfactorIndex}_${questionIndex}`,
                text: question.text || question.question || '',
                dimension: dimension.name || `Dimension ${dimIndex + 1}`,
                dimensionId: dimension.id || dimIndex,
                subfactor: subfactor.name || `Subfactor ${subfactorIndex + 1}`,
                subfactorId: subfactor.id || subfactorIndex,
                questionIndex: questionIndex,
                originalQuestion: question
              });
            });
          }
        });
      }
    });
    surveyQues = {
      questions: questionStructure,
      sort_order: questionStructure?.sort_order || null
    };
  }
  return {
    questionStructure,
    surveyStructure,
    standards,
    extractedQuestions,
    surveyQues
  };
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  padding: 24px;
`;

const PageHeader = styled.div`
  text-align: left;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #6c757d;
  margin: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${props => props.selected ? '#e8f5e8' : '#f5f5f5'};
  color: ${props => props.selected ? '#2d5016' : '#666'};
  font-size: 12px;
  font-weight: 500;
`;

const PulseSummaryCard = styled.div`
  padding: 16px;
  border-radius: 4px;
  border-left: 4px solid #007bff;
`;

const SummaryTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 8px;
`;

const Crumb = styled.span`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  color: ${props => (props.clickable ? '#2D70E2' : '#6c757d')};
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
  text-align: center;
`;

const SurveySelectionStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [pulseSurveyData, setPulseSurveyData] = useState(null);
  const [selectedPulseSurvey, setSelectedPulseSurvey] = useState(null);
  const [pulseSelectedSport, setPulseSelectedSport] = useState(null);
  const [sportDetermined, setSportDetermined] = useState(false);

  // Redux selectors
  const {
    get_culture_audit_reports,
    get_auth,
    loading,
    fetched_scheduled_task,
    get_employees,
    filters,
    get_employee_categories,
    core_data
  } = useSelector(
    (state) => ({
      get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
      get_auth: state.auth,
      loading: state.debrief_schedule.loading,
      fetched_scheduled_task: state.task_templates?.fetched_scheduled_task,
      get_employees: state.employees,
      filters: state.audit?.new_filters,
      get_employee_categories: state.employee_category,
      core_data: state.audit?.core_data
    }),
    shallowEqual
  );

  // Extract data from navigation state
  useEffect(() => {
    if (location.state) {
      const { pulseSurveyData: pData, selectedPulseSurvey: pulse } = location.state;
      setSelectedPulseSurvey(pulse);
      if (pData) {
        setPulseSurveyData(pData);
        try {
          const raw = pData?.rawData;
          const userTasks = raw?.task_template_user_tasks?.user_tasks || [];
          const sportTask = userTasks.find((t) => t.task_type === "selected_sport");
          let sportValue = sportTask?.value ?? null;
          if (typeof sportValue === "string") {
            try {
              const parsed = JSON.parse(sportValue);
              sportValue = parsed || sportValue;
            } catch (e) {
              // If parsing fails, fall back to the raw string
            }
          }
          setPulseSelectedSport(sportValue || null);
          setSportDetermined(true);
        } catch (e) {
          setPulseSelectedSport(null);
          setSportDetermined(true);
        }
      }
    }
  }, [location.state]);

  // Load audit reports on component mount
  useEffect(() => {
    if (get_auth?.organization_id) {
      dispatch({
        type: SG_GET_CULTURE_AUDIT_REPORTS,
        payload: `audit-type-id=5&organization=${get_auth?.organization_id}`,
      });
    }
  }, [dispatch, get_auth?.organization_id]);

  // If we only have the pulse selection (from landing) but no pulse data, fetch and build it
  useEffect(() => {
    if (selectedPulseSurvey && !pulseSurveyData) {
      dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: selectedPulseSurvey.id });
    }
  }, [dispatch, selectedPulseSurvey, pulseSurveyData]);

  // When fetched_scheduled_task arrives, process into pulseSurveyData
  useEffect(() => {
    if (fetched_scheduled_task && selectedPulseSurvey && !pulseSurveyData && get_employees?.employees) {
      // Verify the fetched task matches what we requested (prevents stale data issues)
      if (fetched_scheduled_task.id !== selectedPulseSurvey.id) {
        return;
      }
      
      try {
        const recent = getRecentResponses(
          fetched_scheduled_task,
          get_employees,
          filters || [],
          get_employee_categories?.employee_org_category || []
        );
        const participants = Object?.values(
          fetched_scheduled_task?.user_requests?.user_requests || {}
        ) || [];
        const assignedEmployees = fetched_scheduled_task?.assigned_employees || [];
        const missingParticipants = assignedEmployees.filter(employee =>
          !participants.some(participant => participant.id === employee)
        );
        const missingParticipantsObjects = get_employees?.employees?.filter(employee =>
          missingParticipants.includes(employee.id)
        ) || [];
        missingParticipantsObjects.forEach(employee => {
          participants.push({
            id: employee.id,
            email: employee.email || '',
            email_sent: false,
            first_name: employee.first_name || '',
            status: "Pending"
          });
        });
        const questions = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.filter(
          (task) => task.task_type === "question"
        ) || [];
        participants.forEach((participant) => {
          const employeeObj = get_employees?.employees?.find(
            (emp) => emp.id === participant.id
          );
          participant.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
        });
        const complianceData = calculateCompliance(participants, recent);
        const surveyStructureData = buildSurveyStructureData(core_data);
        const processedData = {
          responses: recent,
          participants,
          questions,
          compliance: complianceData,
          surveyInfo: {
            title: fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "Unnamed Survey",
            description: fetched_scheduled_task?.description || "",
            createdBy: fetched_scheduled_task?.created_by?.name || "",
            createdDate: fetched_scheduled_task?.created_at || "",
            updatedDate: fetched_scheduled_task?.updated_at || "",
            surveyIsAnonymous: fetched_scheduled_task?.is_anonymous || false,
            surveyIsOpen: fetched_scheduled_task?.status !== 51,
            task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
          },
          surveyStructure: {
            questionStructure: surveyStructureData.questionStructure,
            surveyStructure: surveyStructureData.surveyStructure,
            standards: surveyStructureData.standards,
            dimensions: surveyStructureData.questionStructure?.dimensions || [],
            categories: surveyStructureData.surveyStructure?.categories || [],
            surveyType: surveyStructureData.questionStructure?.type || null,
            useLikert: surveyStructureData.questionStructure?.useLikert || false,
            surveyQuestions: surveyStructureData.extractedQuestions || [],
            totalQuestions: surveyStructureData.extractedQuestions?.length || 0,
            completeSurveyQuestions: surveyStructureData.surveyQues || null
          },
          rawData: fetched_scheduled_task
        };
        setPulseSurveyData(processedData);

        // Extract selected sport from the fetched task
        try {
          const raw = fetched_scheduled_task;
          const userTasks = raw?.task_template_user_tasks?.user_tasks || [];
          const sportTask = userTasks.find((t) => t.task_type === "selected_sport");
          let sportValue = sportTask?.value ?? null;
          if (typeof sportValue === "string") {
            try {
              const parsed = JSON.parse(sportValue);
              sportValue = parsed || sportValue;
            } catch (e) {
              // ignore parse error, keep original string
            }
          }
          setPulseSelectedSport(sportValue || null);
          setSportDetermined(true);
        } catch (e) {
          setPulseSelectedSport(null);
          setSportDetermined(true);
        }
      } catch (e) {
        // no-op
      }
    }
  }, [fetched_scheduled_task, selectedPulseSurvey, pulseSurveyData, get_employees, filters, get_employee_categories, core_data]);

  // Handle survey selection
  const handleSurveySelect = useCallback((survey, data) => {
    if (survey && data) {
      const pulse = selectedPulseSurvey || (location.state && location.state.selectedPulseSurvey) || null;
      navigate("/app/otp/otp-comparison/analysis", {
        state: {
          pulseSurveyData: pulseSurveyData || null,
          debriefData: data,
          selectedPulseSurvey: pulse,
          selectedDebrief: survey
        }
      });
    }
  }, [navigate, selectedPulseSurvey, location.state, pulseSurveyData]);

  const isPulseLoading = !pulseSurveyData;

  return (
    <PageContainer>
      <Breadcrumbs>
        <Crumb clickable onClick={() => navigate("/app/otp/AI/pulse-survey")}>Pulse Surveys</Crumb>
        <span>/</span>
        <Crumb>Comparison</Crumb>
        <span>/</span>
        <Crumb>Select survey</Crumb>
      </Breadcrumbs>
      <PageHeader>
        <PageTitle>Select survey for comparison</PageTitle>
        <PageSubtitle>Choose a survey to compare against your selected pulse</PageSubtitle>
      </PageHeader>

      <Container>
        {/* Selected Pulse Survey Summary */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Selected Pulse Survey</SectionTitle>
            <StatusIndicator selected={true}>✓ Selected</StatusIndicator>
          </SectionHeader>
          
          <PulseSummaryCard>
            <SummaryTitle>{selectedPulseSurvey?.name || 'Pulse Survey'}</SummaryTitle>
          </PulseSummaryCard>
        </SectionCard>

        {isPulseLoading && (
          <ErrorMessage>Loading pulse survey data...</ErrorMessage>
        )}

        {/* Survey Selection */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Select Survey for Comparison</SectionTitle>
            <StatusIndicator selected={!!selectedSurvey}>
              {selectedSurvey ? '✓ Selected' : 'Not Selected'}
            </StatusIndicator>
          </SectionHeader>
          
          <DebriefSelector
            auditReports={get_culture_audit_reports}
            onSelect={handleSurveySelect}
            selectedDebrief={selectedSurvey}
            loading={loading}
            pulseSelectedSport={pulseSelectedSport}
            sportDetermined={sportDetermined}
            autoSelectWhenSingle={true}
          />
        </SectionCard>
      </Container>
    </PageContainer>
  );
};

export default SurveySelectionStep;
